import { useField } from "formik";
import { Input as MantineInput, type InputClearButtonFactory } from "@mantine/core";
import { componentWrapper, polymorphicComponentWrapper } from "../../utilities";
import styles from "./input.module.scss";
import type {
    CustomInputFactory,
    CustomInputFormikFactory,
    CustomInputWrapperFactory,
    CustomInputWrapperFormikFactory,
    IInputClearButtonProps,
    IInputFormikProps,
    IInputWrapperFormikProps,
    IInputWrapperProps,
    IProps,
} from "./input.types";
import classnames from "classnames";
import { Group } from "../group";

/**
 * Input component with optional clear button and active state.
 * @param {boolean} props.active - Whether the input is in an active state.
 * @param {object} props.classNames - Custom class names for the input elements.
 * @param {React.ReactNode} props.rightSection - Content to display on the right side of the input.
 * @param {boolean} props.isClearable - Whether the input shows a clear button.
 * @param {boolean} props.activable - Whether the input can be activated.
 * @param {function} props.onClear - Callback function when the input is cleared.
 * @returns {JSX.Element} The Input component.
 */
const Input = polymorphicComponentWrapper<CustomInputFactory, IProps>(
    "Input",
    ({ active, classNames, rightSection, isClearable, activable, onClear, ...otherProps }, ref) => {
        return (
            <MantineInput
                wrapperProps={{ "data-active": active }}
                data-activable={activable}
                classNames={{
                    input: classnames(styles["input"], typeof classNames === "object" ? classNames?.input : ""),
                    wrapper: classnames(
                        styles["inputWrapper"],
                        typeof classNames === "object" ? classNames?.wrapper : ""
                    ),
                    section: classnames(styles["section"], typeof classNames === "object" ? classNames?.section : ""),
                }}
                {...otherProps}
                ref={ref}
                rightSection={
                    isClearable || rightSection ? (
                        <Group gap="sm" w="100%" justify="flex-end" pr="lg">
                            {isClearable && (
                                <InputClearButton
                                    onClick={onClear}
                                    bg="var(--surface-gray-transparent)"
                                    data-testid="clear-input-button"
                                />
                            )}
                            {rightSection}
                        </Group>
                    ) : undefined
                }
                rightSectionWidth={otherProps.rightSectionWidth ?? (isClearable && rightSection ? 58 : undefined)}
            />
        );
    }
);

/**
 * Input wrapper component for grouping input with label and error messages.
 * @param {string} props.variant - The visual style variant of the input wrapper.
 * @param {string} props.color - The color theme of the input wrapper.
 * @returns {JSX.Element} The InputWrapper component.
 */
const InputWrapper = componentWrapper<CustomInputWrapperFactory, IInputWrapperProps>(
    "InputWrapper",
    ({ variant = "default", color, ...otherProps }, ref) => {
        return (
            <MantineInput.Wrapper
                ref={ref}
                classNames={{ root: styles["wrapper"], label: styles["label"] }}
                variant={variant}
                data-variant={variant}
                data-color={color}
                {...otherProps}
            />
        );
    }
);

/**
 * Input wrapper component integrated with Formik for form validation.
 * @param {string} props.name - The name of the input field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @param {string} props.variant - The visual style variant of the input wrapper.
 * @returns {JSX.Element} The InputWrapperFormik component.
 */
const InputWrapperFormik = componentWrapper<CustomInputWrapperFormikFactory, IInputWrapperFormikProps>(
    "InputWrapperFormik",
    ({ name, validate, variant = "filled", ...otherProps }, ref) => {
        const [, meta] = useField({ name, validate });
        return (
            <Input.Wrapper ref={ref} variant={variant} error={meta.touched ? meta.error : undefined} {...otherProps} />
        );
    }
);

/**
 * Input component integrated with Formik for form handling and validation.
 * @param {string} props.label - The label text for the input.
 * @param {string} props.description - The description text below the label.
 * @param {string} props.name - The name of the input field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @param {string} props.type - The input type (text, email, password, etc.).
 * @param {string} props.wrapperVariant - The visual style variant of the wrapper.
 * @param {string} props.variant - The visual style variant of the input.
 * @returns {JSX.Element} The InputFormik component.
 */
const InputFormik = polymorphicComponentWrapper<CustomInputFormikFactory, IInputFormikProps>(
    "InputWrapperFormik",
    (
        { label, description, name, validate, type, wrapperVariant = "filled", variant = "filled", ...otherProps },
        ref
    ) => {
        const [field, meta] = useField({ name, type, validate });
        return (
            <Input.Wrapper
                variant={wrapperVariant}
                label={label}
                description={description}
                error={meta.touched ? meta.error : undefined}
            >
                <Input ref={ref} type={type} variant={variant} {...otherProps} {...field} />
            </Input.Wrapper>
        );
    }
);

InputFormik.Wrapper = InputWrapperFormik;

/**
 * Clear button component for inputs that allows users to reset the input value.
 * @returns {JSX.Element} The InputClearButton component.
 */
const InputClearButton = componentWrapper<InputClearButtonFactory, IInputClearButtonProps>(
    "InputClearButton",
    (props, ref) => {
        return <MantineInput.ClearButton ref={ref} classNames={{ root: styles["clear-button"] }} {...props} />;
    }
);

Input.Label = MantineInput.Label;
Input.Error = MantineInput.Error;
Input.Description = MantineInput.Description;
Input.Placeholder = MantineInput.Placeholder;
Input.Wrapper = InputWrapper;
Input.ClearButton = InputClearButton;
Input.Formik = InputFormik;

export { Input, InputFormik, InputWrapperFormik, InputClearButton, InputWrapper };
